import {Injectable} from '@angular/core';
import {map, Observable, of, tap} from 'rxjs';
import {HttpClient, HttpStatusCode} from '@angular/common/http';
import {catchError} from 'rxjs/operators';
import {NotificationService} from '@shared/notification/notification.service';
import {ActivityDTO, SubmitActivityFeedbackRequest, UpdateActivityRequest} from '@domain/activity/activity.dto';
import {ApiUtil} from '@shared/util/apiUtil';
import {CheckIcon, MailXIcon} from 'lucide-angular';

@Injectable({
  providedIn: 'root'
})
export class ActivityApi {

  constructor(
    private readonly http: HttpClient,
    private readonly notifyService: NotificationService
  ) { }

  public getAllForMe(): Observable<ActivityDTO[]> {
    return this.http.get<ActivityDTO[]>(ApiUtil.buildUrl(this.getPath()));
  }

  public getDetails(activityId?: string | null): Observable<ActivityDTO | null> {
    if (!activityId) {
      return of(null);
    }
    return this.http.get<ActivityDTO>(ApiUtil.buildUrl(this.getPath(activityId)));
  }

  public getExpiring(): Observable<ActivityDTO[] | null> {
    return this.http.get<ActivityDTO[]>(ApiUtil.buildUrl(this.getPath(), '/expiring'));
  }

  public getUntagged(): Observable<ActivityDTO[] | null> {
    return this.http.get<ActivityDTO[]>(ApiUtil.buildUrl(this.getPath(), '/untagged'));
  }

  public update(activityId: string, activity: UpdateActivityRequest): Observable<ActivityDTO> {
    return this.http.patch<ActivityDTO>(ApiUtil.buildUrl(this.getPath(activityId)), activity);
  }

  public submit(activityId: string): Observable<boolean> {
    return this.http.post<void>(ApiUtil.buildUrl(this.getPath(activityId), '/submit'), {}).pipe(
      map(() => true),
      catchError(err => {
        if (err?.status === HttpStatusCode.Conflict) {

          this.notifyService.toast({
            message: 'Oups: l\'activité est déjà publiée?'
          });
        }
        return of(false);
      })
    );
  }

  public publish(activityId: string): Observable<boolean> {
    return this.http.post<void>(ApiUtil.buildUrl(this.getPath(activityId), '/publish'), {}).pipe(
      map(() => true),
      catchError(err => {
        if (err?.status === HttpStatusCode.Conflict) {
          this.notifyService.toast({
            message: 'Oups: ton adresse email n\'est pas encore validée?',
            icon: MailXIcon
          });
        }
        return of(false);
      })
    );
  }

  public unpublish(activityId: string): Observable<ActivityDTO | null> {
    return this.http.post<ActivityDTO | null>(ApiUtil.buildUrl(this.getPath(activityId), '/unpublish'), {}).pipe(
      tap(() => {
        this.notifyService.toast({
          message: 'L\'activité a été dépubliée.',
          icon: CheckIcon
        });
      })
    );
  }

  public delete(activityId: string): Observable<boolean> {
    return this.http.delete<void>(ApiUtil.buildUrl(this.getPath(activityId)), {}).pipe(
      tap(() => {
        this.notifyService.toast({
          message: 'L\'activité a été supprimée.',
          icon: CheckIcon
        });
      }),
      map(() => true)
    );
  }

  public submitFeedback(activityId: string, request: SubmitActivityFeedbackRequest): Observable<boolean> {
    return this.http.post<void>(ApiUtil.buildUrl(this.getPath(activityId), '/feedback'), request).pipe(
      map(() => true),
      catchError(err => {
        return of(false);
      })
    );
  }

  public sendExpiredReminder(activityId: string): Observable<void> {
    return this.http.post<void>(ApiUtil.buildUrl(this.getPath(activityId), '/send-expired-reminder'), {});
  }

  public sendExpiringReminder(activityId: string): Observable<void> {
    return this.http.post<void>(ApiUtil.buildUrl(this.getPath(activityId), '/send-expiring-reminder'), {});
  }

  public getThumbnailUrl(activityId: string): string {
    return ApiUtil.buildUrl(this.getPath(activityId), '/thumbnail');
  }

  public getUnverified(): Observable<ActivityDTO[]> {
    return this.http.get<ActivityDTO[]>(ApiUtil.buildUrl(this.getPath(), '/unverified'));
  }

  public verify(activityId: string): Observable<void> {
    return this.http.post<void>(ApiUtil.buildUrl(this.getPath(activityId), '/verify'), {});
  }

  public getCoverUrl(activityId: string): string {
    return ApiUtil.buildUrl(this.getPath(activityId), '/cover');
  }

  public addTag(activityId: string, tagId: string): Observable<ActivityDTO> {
    return this.http.post<ActivityDTO>(ApiUtil.buildUrl(this.getPath(activityId), `/tag/${tagId}`), null);
  }

  public removeTag(activityId: string, tagId: string): Observable<ActivityDTO> {
    return this.http.delete<ActivityDTO>(ApiUtil.buildUrl(this.getPath(activityId), `/tag/${tagId}`));
  }

  private getPath(activityId?: string): string {
    const prefix = 'activity';
    return activityId
      ? `${prefix}/${activityId}`
      : prefix;
  }
}
